import { createReducer } from '@reduxjs/toolkit';
import {
  fetchQuestionSet,
  getAnalyticsToken,
  fetchPracticeList,
  fetchCohortsQuestionSet,
  fetchCohorts,
  resetQuestionSets,
  fetchAccessibleCourses,
  fetchSurveyQuestions,
  hasExistingConfiguration,
  surveyCharts,
  distributionCharts,
  setCurrentConfigurationId,
  surveyStats,
  updateConfiguration,
} from 'redux/actions/analytics';
import { AnalyticsDashBoardType, ConfigurationType, QrveyState } from 'redux/schemas/app/analytics';
import { initialRootState } from '.';

export const initialQrveyState: QrveyState = {
  isLoading: false,
  type: AnalyticsDashBoardType.UNSET,
};

export const initialAnalyticsState = {
  questionSets: {},
  practiceList: [],
  questionSetsLoaded: false,
  isPracticeListLoaded: false,
  cohorts: [],
  isCohortsListLoaded: false,
  accessibleCourses: [],
  surveyQuestions: {},
  loadedSurveyQuestions: {},
  existingConfiguration: {},
  surveyCharts: {},
  surveyStats: {},
  distributionCharts: {},
  currentPrePostConfigurationId: null,
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(getAnalyticsToken.pending, (state, action) => {
      state.app.qrveyConfig.isLoading = true;
    })
    .addCase(getAnalyticsToken.fulfilled, (state, action) => {
      state.app.qrveyConfig = { ...action.payload, type: action.meta.arg.type, isLoading: false };
    })
    .addCase(fetchQuestionSet.fulfilled, (state, action) => {
      const { catalogId, filterByType } = action.meta.arg;
      if (filterByType) {
        if (!state.app.analytics.surveyQuestionsets) {
          state.app.analytics.surveyQuestionsets = {};
        }
        if (!state.app.analytics.surveyQuestionsets[catalogId]) {
          state.app.analytics.surveyQuestionsets[catalogId] = { course: {}, cohort: {} };
        }
        state.app.analytics.surveyQuestionsets[catalogId].course = action.payload;
      } else {
        if (!state.app.analytics.questionSets) {
          state.app.analytics.questionSets = {};
        }
        if (!state.app.analytics.questionSets[catalogId]) {
          state.app.analytics.questionSets[catalogId] = { course: {}, cohort: {} };
        }

        state.app.analytics.questionSets[catalogId].course = action.payload;
        state.app.analytics.questionSetsLoaded = true;
      }
    })
    .addCase(fetchCohortsQuestionSet.fulfilled, (state, action) => {
      const { catalogId, filterByType } = action.meta.arg;
      if (filterByType) {
        if (!state.app.analytics.surveyQuestionsets) {
          state.app.analytics.surveyQuestionsets = {};
        }
        if (!state.app.analytics.surveyQuestionsets[catalogId]) {
          state.app.analytics.surveyQuestionsets[catalogId] = { course: {}, cohort: {} };
        }
        state.app.analytics.surveyQuestionsets[catalogId].cohort = action.payload;
      } else {
        if (!state.app.analytics.questionSets) {
          state.app.analytics.questionSets = {};
        }
        if (!state.app.analytics.questionSets[catalogId]) {
          state.app.analytics.questionSets[catalogId] = { course: {}, cohort: {} };
        }
        state.app.analytics.questionSets[catalogId].cohort = action.payload;
        state.app.analytics.questionSetsLoaded = true;
      }
    })
    .addCase(fetchPracticeList.fulfilled, (state, action) => {
      state.app.analytics.practiceList = action.payload;
      state.app.analytics.isPracticeListLoaded = true;
    })
    .addCase(fetchPracticeList.pending, (state, action) => {
      state.app.analytics.isPracticeListLoaded = false;
    })
    .addCase(fetchCohorts.pending, (state) => {
      state.app.analytics.isCohortsListLoaded = false;
    })
    .addCase(fetchCohorts.fulfilled, (state, action) => {
      state.app.analytics.cohorts = action.payload;
      state.app.analytics.isCohortsListLoaded = true;
    })
    .addCase(resetQuestionSets, (state) => {
      state.app.analytics.questionSets = {};
      state.app.analytics.questionSetsLoaded = false;
    })
    .addCase(fetchAccessibleCourses.fulfilled, (state, action) => {
      state.app.analytics.accessibleCourses = action.payload || [];
    })
    .addCase(fetchSurveyQuestions.fulfilled, (state, action) => {
      const { questionSetId } = action.meta.arg;

      state.app.analytics.loadedSurveyQuestions[questionSetId] = true;
      state.app.analytics.surveyQuestions[questionSetId] = action.payload;
    })
    .addCase(hasExistingConfiguration.fulfilled, (state, action) => {
      const { preCourseCatalogId, configurationType } = action.meta.arg;

      if (!state.app.analytics.existingConfiguration) {
        state.app.analytics.existingConfiguration = {};
      }
      if (!state.app.analytics.existingConfiguration[preCourseCatalogId]) {
        state.app.analytics.existingConfiguration[preCourseCatalogId] = [];
      }
      if (action.payload?.length === 2) {
        // When both cohort and course configurations exist
        // copy the payload and mark them as loaded
        state.app.analytics.existingConfiguration[preCourseCatalogId] = action.payload.map(config => ({ ...config, isLoaded: true }));
      }
      if (action.payload?.length === 1) {
        // When only one configuration exists, add the other type as loaded but empty
        // to avoid refetching configurations
        const configurations = action.payload.map(config => ({ ...config, isLoaded: true }));
        if (configurations[0]?.configurationType !== configurationType) {
          configurations.push({ configurationType, isLoaded: true });
        }
        state.app.analytics.existingConfiguration[preCourseCatalogId] = configurations;
      }
      if (action.payload?.length === 0) {
        // When no configurations exist, add both types as loaded but empty
        state.app.analytics.existingConfiguration[preCourseCatalogId] = [
          { configurationType: ConfigurationType.course, isLoaded: true },
          { configurationType: ConfigurationType.cohort, isLoaded: true },
        ];
      }
    })
    .addCase(surveyCharts.fulfilled, (state, action) => {
      const { configurationId } = action.meta.arg;
      state.app.qrveyConfig.isLoading = false;
      state.app.analytics.surveyCharts[configurationId] = { shouldReload: false };
    })
    .addCase(distributionCharts.fulfilled, (state, action) => {
      const { configurationId } = action.meta.arg;
      state.app.analytics.distributionCharts[configurationId] = action.payload;
    })
    .addCase(setCurrentConfigurationId, (state, action) => {
      state.app.analytics.currentPrePostConfigurationId = action.payload;
    })
    .addCase(surveyStats.fulfilled, (state, action) => {
      const { configurationId } = action.meta.arg;
      const engagementPercentage = action.payload?.totalResponders !== null
          && action.payload?.totalResponders !== undefined
          && action.payload?.totalLearners > 0
        ? ((action.payload.totalResponders / action.payload.totalLearners) * 100)
        : 0;
      state.app.analytics.surveyStats[configurationId] = {
        ...action.payload,
        engagementPercentage: engagementPercentage % 1 === 0 // check if whole number
          ? engagementPercentage
          : engagementPercentage.toFixed(2) as unknown as number,
      };
    })
    .addCase(updateConfiguration.fulfilled, (state, action) => {
      const { configurationId } = action.meta.arg;
      if (!state.app.analytics.surveyCharts[configurationId]) {
        state.app.analytics.surveyCharts[configurationId] = {};
      }
      state.app.analytics.surveyCharts[configurationId].shouldReload = true;
    });
});
